import { StatelessComponent } from 'react'
import * as cn from 'classnames'
import * as styles from 'styles/pages/Styleguide.css'
import Logo from 'components/features/pfm/Logo/Logo'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { SG_ATOMS_URL, SG_META_URL, SG_MOLECULES_URL, SG_ORGANISMS_URL, SG_THEMES_URL, SG_WORKSPACE_URL } from 'tangle-shared'
import { StyleguideTargetType } from 'interfaces/styleguide'
import { withRouter, RouteComponentProps } from 'react-router-dom'

export type StyleguideProps = RouteComponentProps<{}>

const StyleguideComponent: StatelessComponent<StyleguideProps> = (props) => {
  const { children, location } = props
  const url = location.pathname

  const onMetaPage = url === SG_META_URL
  const onAtomsPage = url === SG_ATOMS_URL
  const onMoleculesPage = url === SG_MOLECULES_URL
  const onOrganismsPage = url === SG_ORGANISMS_URL
  const onThemesPage = url === SG_THEMES_URL
  const onWorkspacePage = url === SG_WORKSPACE_URL

  type SGNavLink = { to: StyleguideTargetType, text: string }

  const metaLinks: SGNavLink[] = [
    { to: 'meta_colors', text: 'Colors' },
    { to: 'meta_themes', text: 'Themes' },
    { to: 'meta_fonts', text: 'Fonts' },
    { to: 'meta_text', text: 'Text' },
    { to: 'meta_logos', text: 'Logos' },
    { to: 'meta_layouts', text: 'Layouts' },
    { to: 'meta_shadows', text: 'Shadows' },
  ]

  const atomLinks: SGNavLink[] = [
    { to: 'atoms_blocks', text: 'Blocks' },
    { to: 'atoms_buttons', text: 'Buttons' },
    { to: 'atoms_inputs', text: 'Inputs' },
    { to: 'atoms_icons', text: 'Icons' },
    { to: 'atoms_charts', text: 'Charts' },
    { to: 'atoms_balloons', text: 'Balloons' },
  ]

  const moleculeLinks: SGNavLink[] = [
    { to: 'molecules_post_input', text: 'Post Input' },
    { to: 'molecules_modules', text: 'Modules' },
    { to: 'molecules_tables', text: 'Tables' },
  ]

  const organismLinks: SGNavLink[] = [
    { to: 'organisms_insights', text: 'Insights' },
    { to: 'organisms_highlights', text: 'Highlights' },
  ]

  const themeLinks: SGNavLink[] = [
    { to: 'themes_primary', text: 'Primary' },
    { to: 'themes_light_primary', text: 'Light Primary' },
    { to: 'themes_dark_primary', text: 'Dark Primary' },
    { to: 'themes_feature', text: 'Feature' },
    { to: 'themes_brand', text: 'Brand' },
    { to: 'themes_neutral', text: 'Neutral' },
  ]

  const renderNavLink = ({ to, text }: SGNavLink) => (
    <li key={ to }>
      <NavigationLink to={ to }>
        <button className={ styles.secondaryNavButton }>{ text }</button>
      </NavigationLink>
    </li>
  )

  const styleGuideNav = (
    <nav className={ cn(styles.sgNav, styles.midnight) }>
      <ul className={ styles.navList }>
        <li className={ styles.logoWrapper }>
          <Logo target='dashboard' theme='white' size='regular' />
        </li>
        <li className={ cn(styles.withDropdown, { [styles.dawn]: onMetaPage }) }>
          <NavigationLink to='meta'>
            <button className={ styles.navButton }>Meta</button>
          </NavigationLink>
          <ul className={ cn(styles.sgSecondaryNav, styles.dawn, { [styles.visible]: onMetaPage }) }>
            { metaLinks.map(renderNavLink) }
          </ul>
        </li>
        <li className={ cn(styles.withDropdown, { [styles.dawn]: onAtomsPage }) }>
          <NavigationLink to='atoms'>
            <button className={ styles.navButton }>Atoms</button>
          </NavigationLink>
          <ul className={ cn(styles.sgSecondaryNav, styles.dawn, { [styles.visible]: onAtomsPage }) }>
            { atomLinks.map(renderNavLink) }
          </ul>
        </li>
        <li className={ cn(styles.withDropdown, { [styles.dawn]: onMoleculesPage }) }>
          <NavigationLink to='molecules'>
            <button className={ styles.navButton }>Molecules</button>
          </NavigationLink>
          <ul className={ cn(styles.sgSecondaryNav, styles.dawn, { [styles.visible]: onMoleculesPage }) }>
            { moleculeLinks.map(renderNavLink) }
          </ul>
        </li>
        <li className={ cn(styles.withDropdown, { [styles.dawn]: onOrganismsPage }) }>
          <NavigationLink to='organisms'>
            <button className={ styles.navButton }>Organisms</button>
          </NavigationLink>
          <ul className={ cn(styles.sgSecondaryNav, styles.dawn, { [styles.visible]: onOrganismsPage }) }>
            { organismLinks.map(renderNavLink) }
          </ul>
        </li>
        <li className={ cn(styles.withDropdown, { [styles.dawn]: onThemesPage }) }>
          <NavigationLink to='themes'>
            <button className={ styles.navButton }>Themes</button>
          </NavigationLink>
          <ul className={ cn(styles.sgSecondaryNav, styles.dawn, { [styles.visible]: onThemesPage }) }>
            { themeLinks.map(renderNavLink) }
          </ul>
        </li>
        <li className={ cn({ [styles.dawn]: onWorkspacePage }) }>
          <NavigationLink to='workspace'>
            <button className={ styles.navButton }>Workspace</button>
          </NavigationLink>
        </li>
      </ul>
    </nav>
  )

  return (
    <div className={ styles.styleGuide }>
      { styleGuideNav }
      { children }
    </div>
  )
}


const Styleguide = withRouter(StyleguideComponent)
export default Styleguide
